'use client';

// Shared accessible dialog primitive for HomeV2. One source of truth for the
// overlay + container + header scaffold every modal used to hand-roll, plus the
// accessibility every modal needs: role/aria-modal, initial focus, Escape to
// close, a Tab focus-trap, and focus-return to the opener on close.
//
// Callers pass their own padded body as children; pass title=null to skip the
// header row entirely (e.g. a modal that draws its own custom chrome). The
// Escape keydown mirrors the capture-phase approach in CommandPalette.js.
// draggable (3 sep 2026): de kop wordt een greep en het venster schuift mee,
// voor vensters die je midden in een gesprek opzij wilt zetten (het logvenster).
// Het scherm erachter blijft gedimd en niet klikbaar; alleen de plek verandert.

import React, { useEffect, useLayoutEffect, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { HV2, SURFACE, modalScrim, modalRadius, t, appConfirm, EntityIcon, useDraftGuard } from './lib';
import useWindowDrag from './useWindowDrag';
import { registerModalClose } from '@/lib/modal-close.mjs';
import { registerKeyboardLayer, trapLayerTab } from '@/lib/keyboard-layers.mjs';

// Een venster, een scroller (venstersaudit ronde 2, 20 okt 2026). Een lijst met
// een eigen maxHeight binnen het venster van 88vh gaf twee scrollbalken boven
// elkaar. Kies per venster: laat de lijst gewoon doorlopen (het venster scrollt),
// of zet pane op het venster als zoekveld en knoppen in beeld moeten blijven.
// Dan is het venster een kolom; elke tussenlaag krijgt MODAL_PANE en alleen het
// deel met MODAL_SCROLL scrollt. Bewaakt door lib/modal-layout.test.mjs.
export const MODAL_PANE = Object.freeze({ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 });
export const MODAL_SCROLL = Object.freeze({ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' });

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export default function Modal({ open = true, onClose, title, headerExtra, recordType, width = '460px', align = 'center', zIndex = 600, confirmDirty, historyKey, draggable = false, portal = false, pane = false, children }) {
  const containerRef = useRef(null);
  const drag = useWindowDrag(open);
  const titleId = useId(); // labels the dialog via aria-labelledby when a title renders

  // Latest close-related props, read by the stable requestClose below so the
  // Escape handler (bound once per open) never closes over a stale dirty-check.
  const cbRef = useRef({ onClose, confirmDirty });
  cbRef.current = { onClose, confirmDirty };
  // Een open modal met getypte tekst houdt het automatisch herladen bij een nieuwe
  // versie tegen (VersionWatcher). Alleen voor herladen: de eigen sluitvraag blijft confirmDirty.
  useDraftGuard(open && (typeof confirmDirty === 'function' ? !!confirmDirty() : !!confirmDirty), { reloadOnly: true });

  // Guards a second requestClose while the app confirm dialog is already open, so
  // a held/double Escape (or an extra backdrop click) can't stack dialogs.
  const confirmingRef = useRef(false);

  // Guarded close. When a caller passes confirmDirty (bool or () => bool) and it
  // reports unsaved input, confirm before discarding - so an accidental backdrop
  // click or Escape can't silently wipe typed data. No guard = close immediately.
  // Uses the app-styled, non-blocking appConfirm (via the globally mounted
  // AppDialogHost) rather than the native window.confirm, which freezes the
  // renderer under CDP automation and clashes with the app chrome for users.
  // De ene sluitvraag van dit venster: sluiten (x, Escape, scrim, Back) en
  // wegnavigeren (andere hash, ander record) vragen allebei hiermee.
  // Loopt er al een vraag, dan telt een tweede als Annuleren.
  const confirmDiscard = useCallback(async () => {
    if (confirmingRef.current) return false;
    confirmingRef.current = true;
    try { return await appConfirm(t('Je hebt niet-opgeslagen wijzigingen. Sluiten en verwerpen?')); }
    finally { confirmingRef.current = false; }
  }, []);
  const requestClose = useCallback(async () => {
    if (confirmingRef.current) return;
    const { onClose: oc, confirmDirty: cd } = cbRef.current;
    const dirty = typeof cd === 'function' ? cd() : !!cd;
    if (dirty && !(await confirmDiscard())) return;
    if (oc) oc();
  }, [confirmDiscard]);

  // Elke open modal staat in het sluitregister (lib/modal-close.mjs), ook zonder
  // historyKey: dan onder een eigen sleutel. De navigatiewacht in app/page.js
  // leest daaruit of er een vuile modal open staat en vraagt via confirmDiscard.
  // Layout-effect: bij sluiten is de registratie in dezelfde commit weg, zodat
  // een navigatie direct na opslaan niet nog een keer vraagt.
  const ownKey = useId();
  useLayoutEffect(() => {
    if (!open) return undefined;
    return registerModalClose(historyKey || `modal${ownKey}`, {
      isDirty: () => {
        const dirty = cbRef.current.confirmDirty;
        return typeof dirty === 'function' ? !!dirty() : !!dirty;
      },
      requestClose,
      confirmDiscard,
    });
  }, [open, historyKey, ownKey, requestClose, confirmDiscard]);

  // On open: remember the opener, focus the first focusable element inside, and
  // restore focus to the opener on unmount/close.
  useEffect(() => {
    if (!open) return undefined;
    const opener = typeof document !== 'undefined' ? document.activeElement : null;
    const focusables = () => Array.from(containerRef.current ? containerRef.current.querySelectorAll(FOCUSABLE) : []).filter((el) => el.offsetParent !== null);
    const first = focusables()[0];
    if (first) first.focus();
    return () => { if (opener && typeof opener.focus === 'function') opener.focus(); };
  }, [open]);

  // Only the top visible layer handles Escape/Tab, including portaled menus.
  useEffect(() => {
    if (!open) return undefined;
    return registerKeyboardLayer({
      element: () => containerRef.current, zIndex, onEscape: requestClose,
      onTab: (e) => trapLayerTab(e, containerRef.current),
    });
  }, [open, requestClose, zIndex]);

  // Ontwikkelwaarschuwing (audit 2 september 2026): een venster mag nooit
  // zijwaarts scrollen. Zodra de inhoud breder is dan de container staat het
  // hier in de console, met titel en overschot, in plaats van pas bij een
  // gebruiker die de rechterrand mist.
  useEffect(() => {
    if (!open || process.env.NODE_ENV === 'production') return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const check = () => {
      const over = el.scrollWidth - el.clientWidth;
      if (over > 1) console.warn(`[Modal] "${title || '(zonder titel)'}" is ${over}px te smal voor zijn inhoud (breedte ${width}); de inhoud scrollt zijwaarts.`);
    };
    const id = window.setTimeout(check, 250);
    return () => window.clearTimeout(id);
  }, [open, title, width]);

  if (!open) return null;

  // Een getal als breedte gaf min(1100, 96vw): ongeldige CSS, het venster kromp tot zijn inhoud.
  const w = typeof width === 'number' ? `${width}px` : width;
  // portal: voor een venster dat in een rastercel of een strook met eigen stapelcontext
  // gemount wordt; daar erfde het whiteSpace nowrap en zat zijn zIndex gevangen
  // (venstersaudit 20 okt 2026). Niet standaard: 38 buitenklik-sluiters lezen
  // contains(e.target) en zouden een klik in een geportaald venster als buiten zien.
  const node = (
    <div
      data-pa-overlay="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title != null ? titleId : undefined}
      onMouseDown={requestClose}
      style={{ position: 'fixed', inset: 0, zIndex, background: modalScrim, display: 'flex', justifyContent: 'center', alignItems: align === 'top' ? 'flex-start' : 'center', padding: 20 }}
    >
      <div
        ref={containerRef}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ width: `min(${w}, 96vw)`, maxHeight: '88vh', overflowY: 'auto', ...(pane ? { display: 'flex', flexDirection: 'column' } : null), ...SURFACE.modal, transform: draggable ? drag.transform : undefined }}
      >
        {title != null && (
          <div onPointerDown={draggable ? drag.onPointerDown : undefined} className="pb-modal-head" style={{ display: 'flex', alignItems: 'center', flexShrink: 0, minHeight: 44, borderBottom: `1px solid ${HV2.divider}`, borderRadius: `${modalRadius - 1}px ${modalRadius - 1}px 0 0`, overflow: 'hidden', cursor: draggable ? 'grab' : undefined, userSelect: draggable ? 'none' : undefined, touchAction: draggable ? 'none' : undefined }}>
            {recordType ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', height: 44, flex: 'none' }}>
                {recordType.lead ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: '100%', padding: '0 11px 0 16px', borderTopLeftRadius: modalRadius - 1, background: recordType.lead.color, color: recordType.lead.bandText }}>
                    <EntityIcon entity={recordType.lead.icon} size={13} color={recordType.lead.bandText} strokeWidth={2.1} />
                    <span style={{ fontSize: HV2.fsText, fontWeight: 650, whiteSpace: 'nowrap' }}>{recordType.lead.label}</span>
                  </span>
                ) : null}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: recordType.lead ? 7 : 8, height: '100%', padding: recordType.lead ? '0 12px' : '0 12px 0 16px', ...(!recordType.lead ? { borderTopLeftRadius: modalRadius - 1 } : {}), background: recordType.color, color: recordType.bandText }}>
                  <EntityIcon entity={recordType.icon} size={13} color={recordType.bandText} strokeWidth={2.1} />
                  <span style={{ fontSize: HV2.fsText, fontWeight: 650, whiteSpace: 'nowrap' }}>{recordType.label}</span>
                </span>
              </span>
            ) : null}
            {/* Smal scherm (venstersaudit 20 okt 2026): de titel kort af met een ellips,
                de × blijft altijd staan en headerExtra zakt onder 480px naar een
                tweede rij (.pb-modal-extra in globals.css). */}
            <span id={titleId} className="pb-modal-title" style={{ fontSize: HV2.fsText, fontWeight: 650, color: HV2.ink, padding: '0 14px', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
            <div className="pb-modal-gap" style={{ flex: 1, minWidth: 0 }} />
            {headerExtra != null && headerExtra !== false ? (
              <div className="pb-modal-extra" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>{headerExtra}</div>
            ) : null}
            <button type="button" onClick={requestClose} aria-label="Sluiten" style={{ flexShrink: 0, width: 28, height: 28, margin: headerExtra != null && headerExtra !== false ? '0 10px 0 8px' : '0 10px 0 0', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 19, color: HV2.muted }} data-knop>×</button>
          </div>
        )}
        {typeof children === 'function' ? children(requestClose) : children}
      </div>
    </div>
  );
  return portal && typeof document !== 'undefined' ? createPortal(node, document.body) : node;
}
